 
import ImageIcon from '@mui/icons-material/Image';
import {
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import fileManagerService from '../../services/file-manager-service';

async function resolveSignedUrls(urls) {
  return Promise.all(
    urls.map((url) =>
      url?.startsWith('https://') ? fileManagerService.getSignedUrl(url) : Promise.resolve(url)
    )
  );
}

function PhotoSection({ title, photos, onImageClick }) {
  const [signedPhotos, setSignedPhotos] = useState([]);

  useEffect(() => {
    if (photos.length > 0) {
      resolveSignedUrls(photos).then(setSignedPhotos);
    } else {
      setSignedPhotos([]);
    }
  }, [photos]);

  if (signedPhotos.length === 0) return null;

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title} ({signedPhotos.length})
        </Typography>
      </Grid>
      {signedPhotos.map((img, i) => (
        <Grid item key={i} xs={2}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: 3,
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.01)' }
            }}
            onClick={() => onImageClick(img)}
          >
            <CardHeader title={`Photo ${i + 1}`} />
            <Box>
              <img
                src={img}
                alt={`${title} ${i + 1}`}
                style={{
                  width: '100%',
                  height: '300px',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </Box>
          </Card>
        </Grid>
      ))}
    </>
  );
}

export default function PackageDetailsMediaTab({
  imageList = [],
  videoList = [],
  shippingLabelPhotos = [],
  packagePhotos = [],
  items = [],
  onImageClick
}) {
  const [signedImages, setSignedImages] = useState([]);
  const [signedVideos, setSignedVideos] = useState([]);

  useEffect(() => {
    if (imageList.length > 0) {
      resolveSignedUrls(imageList).then(setSignedImages);
    } else {
      setSignedImages([]);
    }
  }, [imageList]);

  useEffect(() => {
    if (videoList.length > 0) {
      resolveSignedUrls(videoList).then(setSignedVideos);
    } else {
      setSignedVideos([]);
    }
  }, [videoList]);

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          <ImageIcon sx={{ mr: 1 }} />
          Media Content
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      {/* Shipping Label Photos */}
      <PhotoSection
        title="Shipping Label Photos"
        photos={shippingLabelPhotos}
        onImageClick={onImageClick}
      />

      {/* Package Photos */}
      <PhotoSection
        title="Package Photos"
        photos={packagePhotos}
        onImageClick={onImageClick}
      />

      {/* Image Gallery */}
      {signedImages?.length > 0 && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Images ({signedImages.length})
            </Typography>
          </Grid>
          {signedImages.map((img, i) => (
            <Grid item key={i} xs={2}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: 3,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.01)' }
                }}
                onClick={() => onImageClick(img)}
              >
                <CardHeader title={`Image ${i + 1}`} />
                <Box>
                  <img
                    src={img}
                    alt={`#${i + 1}`}
                    style={{
                      width: '100%',
                      height: '300px',
                      objectFit: 'contain',
                      display: 'block'
                    }}
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </>
      )}

      {/* Video Gallery */}
      {signedVideos?.length > 0 && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Videos ({signedVideos.length})
            </Typography>
          </Grid>
          {signedVideos.map((video, i) => (
            <Grid item key={i} xs={2}>
              <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardHeader title={`Video ${i + 1}`} />
                <Box sx={{ width: '100%' }}>
                  <video
                    controls
                    src={video}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderBottomLeftRadius: 8,
                      borderBottomRightRadius: 8
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </Box>
              </Card>
            </Grid>
          ))}
        </>
      )}

      {/* Item-level Photos */}
      {items.map((item, itemIndex) => {
        const hasFrontViewPhotos = item.front_view_photos?.length > 0;
        const hasLabelBarcodePhotos = item.label_barcode_photos?.length > 0;
        if (!hasFrontViewPhotos && !hasLabelBarcodePhotos) return null;
        return (
          <React.Fragment key={itemIndex}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Item {itemIndex + 1} Photos
                {item.upc ? ` — UPC: ${item.upc}` : ''}
              </Typography>
            </Grid>
            {hasFrontViewPhotos && (
              <PhotoSection
                title={`Item ${itemIndex + 1} Front View`}
                photos={item.front_view_photos}
                onImageClick={onImageClick}
              />
            )}
            {hasLabelBarcodePhotos && (
              <PhotoSection
                title={`Item ${itemIndex + 1} Label/Barcode`}
                photos={item.label_barcode_photos}
                onImageClick={onImageClick}
              />
            )}
          </React.Fragment>
        );
      })}
    </Grid>
  );
}
